import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BoutonPrincipal,
  
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../ui/Primitives';
import { usePerimetre } from '../../site/usePerimetre';
import { TOUS_LES_SITES } from '../../site/context';
import { useSites } from '../../lib/useSites';
import { useReferentiels } from '../../lib/useDocuments';
import {
  useCloturerSeance,
  useCreerSeance,
  useRequalifierPresence,
  useSeances,
  useSignerFormateur,
  useSignerPresence,
  type DemiJournee,
} from '../../lib/useEmargement';
import { ErreurApi } from '../../lib/api';
import { FeuilleEmargement } from '../documents/FeuilleEmargement';
import { ModaleDetailPresence } from './ModaleDetailPresence';

function jourCourant(): string {
  return new Intl.DateTimeFormat('fr-CA', { timeZone: 'Indian/Reunion' }).format(new Date());
}


const TONS_SEANCE: Record<string, string> = {
  PREVUE: 'neutral',
  EN_COURS: 'info',
  CLOTUREE: 'success',
};

const LIBELLES_SEANCE: Record<string, string> = {
  PREVUE: 'Prévue',
  EN_COURS: 'En cours',
  CLOTUREE: 'Clôturée',
};

/**
 * Séances tenues hors du parcours en salle (CDC §6.7).
 *
 * Le parcours de `EmargementPage` couvre la journée de centre : un groupe, une
 * date, deux demi-journées prises au calendrier de la formation. Il ne couvre
 * pas tout — un rattrapage, un module qui réunit deux groupes n'ont pas de
 * session à eux. Cette vue-là reste donc ouverte, avec la création manuelle,
 * la signature du formateur et la clôture qui produit le PV.
 *
 * Une classe à distance ne s'y crée pas : elle se programme dans la « Salle
 * e-learning », dont la salle contrôle la caméra et l'émargement. Elle s'y
 * relit et s'y régularise comme les autres.
 *
 * Elle n'est pas un doublon : elle écrit dans les mêmes `Seance` et `Presence`,
 * et une séance créée ici apparaît dans le suivi comme les autres.
 */
/**
 * Création pré-remplie depuis le tableau de bord du formateur (TF-456) : la
 * feuille du jour d'une promotion d'apprentissage, ses seuls apprentis
 * convoqués. Le formateur la relit et la confirme.
 */
export interface CreationPreremplie {
  formationCode: string;
  site: string;
  demiJournee: DemiJournee;
  heureDebut?: string;
  heureFin?: string;
  libelle: string;
}

export function SeancesHorsSession({
  seanceInitiale = null,
  creationInitiale = null,
}: {
  seanceInitiale?: string | null;
  creationInitiale?: CreationPreremplie | null;
}): JSX.Element {
  const { site } = usePerimetre();
  const [jour, setJour] = useState(jourCourant);
  const [creation, setCreation] = useState(creationInitiale !== null);
  // Séance du jour désignée par l'accès direct du tableau de bord formateur.
  const [seanceOuverte, setSeanceOuverte] = useState<string | null>(seanceInitiale);
  // Détail d'une ligne d'une feuille clôturée : sa correction se demande à la
  // direction (TF-437). La feuille se relit par la liste du jour.
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, isLoading, error } = useSeances(site, jour);
  const creer = useCreerSeance();
  const requalifier = useRequalifierPresence();
  const signer = useSignerPresence();
  const signerFormateur = useSignerFormateur();
  const cloturer = useCloturerSeance();

  const compteurs = data?.compteurs;
  const prestataire = data?.prestataire;
  const seances = data?.seances ?? [];
  const active = seances.find((seance) => seance.id === seanceOuverte) ?? seances[0] ?? null;

  return (
    <div className="space-y-6">
      <EnteteEcran
        titre="Séances hors session"
        sousTitre="Rattrapage, module inter-groupes, classes à distance · PV horodaté pour l’audit"
        actions={
          <>
            <input
              type="date"
              aria-label="Jour affiché"
              value={jour}
              onChange={(evenement) => setJour(evenement.target.value)}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm"
            />
            <BoutonPrincipal onClick={() => setCreation((ouvert) => !ouvert)}>
              {creation ? 'Fermer' : '+ Nouvelle séance'}
            </BoutonPrincipal>
          </>
        }
      />

      {prestataire && !prestataire.operationnel && (
        <div className="rounded-2xl bg-amber-50 p-4 text-sm text-codialis-navy">
          <Pastille texte="Accusé interne" ton="warning" />
          <p className="mt-2">
            Les émargements sont scellés en interne : tracé conservé, horodatage serveur et
            adresse IP. Aucun prestataire de signature électronique n’étant retenu, ils ne
            valent pas signature qualifiée eIDAS. La position de l’appareil n’est pas
            collectée.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Séances du jour"
          valeur={compteurs?.seancesDuJour ?? '—'}
          detail={compteurs ? `${compteurs.cloturees} clôturée(s)` : 'périmètre affiché'}
          ton="navy"
        />
        <Compteur
          libelle="Émargements recueillis"
          valeur={
            compteurs
              ? `${compteurs.signaturesRecueillies} / ${compteurs.signaturesAttendues}`
              : '—'
          }
          detail="signatures apprenants"
          ton="blue"
        />
        <Compteur
          libelle="Taux de présence"
          valeur={
            compteurs?.tauxPresence === null || compteurs?.tauxPresence === undefined
              ? '—'
              : `${compteurs.tauxPresence.toString().replace('.', ',')} %`
          }
          detail="sur les demi-journées émargées"
          ton="mint"
        />
        <Compteur
          libelle="Séance ouverte"
          valeur={active ? LIBELLES_SEANCE[active.statut] ?? active.statut : '—'}
          detail={active ? `${active.heureDebut} · ${active.heureFin}` : 'aucune séance'}
          ton="warm"
        />
      </div>

      {creation && (
        <FormulaireSeance
          enCours={creer.isPending}
          erreur={creer.error instanceof ErreurApi ? creer.error.message : null}
          jour={jour}
          prerempli={creationInitiale}
          onCreer={(entree) =>
            creer.mutate(entree, {
              onSuccess: (seance) => {
                setCreation(false);
                setSeanceOuverte(seance.id);
              },
            })
          }
        />
      )}

      {cloturer.data && (
        <div className="rounded-2xl bg-codialis-sand p-4 text-sm text-codialis-navy">
          {cloturer.data.message}
        </div>
      )}

      {(requalifier.error instanceof ErreurApi ||
        signer.error instanceof ErreurApi ||
        cloturer.error instanceof ErreurApi) && (
        <MessageErreur
          message={
            (requalifier.error instanceof ErreurApi && requalifier.error.message) ||
            (signer.error instanceof ErreurApi && signer.error.message) ||
            (cloturer.error instanceof ErreurApi && cloturer.error.message) ||
            'Action refusée.'
          }
        />
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 min-w-0">
          {error && (
            <MessageErreur
              message={
                error instanceof ErreurApi
                  ? error.message
                  : 'Les séances n’ont pas pu être chargées.'
              }
            />
          )}
          {isLoading && (
            <Carte titre="Feuille d’émargement" sansBordure>
              <Chargement libelle="Chargement des séances…" />
            </Carte>
          )}

          {!isLoading && !error && active === null && (
            <Carte titre="Feuille d’émargement" sansBordure>
              <p className="px-4 sm:px-6 py-10 text-center text-sm text-codialis-gray">
                Aucune séance ce jour sur ce périmètre. Créez-en une pour ouvrir la feuille.
              </p>
            </Carte>
          )}

          {active && (
            <FeuilleEmargement
              seance={active}
              enCours={
                requalifier.isPending ||
                signer.isPending ||
                signerFormateur.isPending ||
                cloturer.isPending
              }
              onRequalifier={(presenceId, statut, justifiee, motif) =>
                requalifier.mutate({
                  seanceId: active.id,
                  presenceId,
                  statut,
                  ...(justifiee === undefined ? {} : { justifiee }),
                  ...(motif ? { motifJustification: motif } : {}),
                })
              }
              onSigner={(presenceId, trace) =>
                signer.mutate({ seanceId: active.id, presenceId, trace })
              }
              onSignerFormateur={(trace) => signerFormateur.mutate({ seanceId: active.id, trace })}
              erreurSignatureFormateur={
                signerFormateur.error instanceof Error ? signerFormateur.error.message : null
              }
              onCloturer={() => cloturer.mutate(active.id)}
              onDetail={setDetailId}
            />
          )}
          {active && (
            <ModaleDetailPresence
              seance={active}
              presenceId={detailId}
              onFermer={() => setDetailId(null)}
              onSeanceMiseAJour={() => undefined}
            />
          )}
        </div>

        <Carte titre="Séances du jour" sansBordure>
          <div className="divide-y divide-gray-100">
            {seances.length === 0 && (
              <p className="px-4 sm:px-6 py-8 text-center text-sm text-codialis-gray">
                Aucune séance ce jour
              </p>
            )}
            {seances.map((seance) => (
              <button
                key={seance.id}
                type="button"
                onClick={() => setSeanceOuverte(seance.id)}
                className={`flex w-full items-center gap-3 px-4 sm:px-6 py-3 text-left transition hover:bg-gray-50 ${
                  active?.id === seance.id ? 'bg-codialis-sand' : ''
                }`}
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-codialis-navy">
                    {seance.libelle}
                  </div>
                  <div className="text-[11px] text-codialis-gray">
                    {seance.demiJourneeLibelle} · {seance.heureDebut} · {seance.siteCode}
                    {seance.type === 'ELEARNING' ? ' · e-learning' : ''}
                  </div>
                </div>
                <Pastille
                  texte={LIBELLES_SEANCE[seance.statut] ?? seance.statut}
                  ton={TONS_SEANCE[seance.statut] ?? 'neutral'}
                />
              </button>
            ))}
          </div>
        </Carte>
      </div>
    </div>
  );
}

const CLASSE_CHAMP = 'w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm';

function FormulaireSeance({
  jour,
  enCours,
  erreur,
  prerempli,
  onCreer,
}: {
  jour: string;
  enCours: boolean;
  erreur: string | null;
  prerempli: CreationPreremplie | null;
  onCreer: (entree: {
    libelle: string;
    site: string;
    date: string;
    demiJournee: DemiJournee;
    heureDebut: string;
    heureFin: string;
    lieu?: string;
    formationCode?: string;
    sansSession?: boolean;
    moduleFormationId?: string;
  }) => void;
}): JSX.Element {
  const { site } = usePerimetre();
  const { data: sites } = useSites();
  const referentiels = useReferentiels();

  const matin = (prerempli?.demiJournee ?? 'MATIN') === 'MATIN';
  const [libelle, setLibelle] = useState(prerempli?.libelle ?? '');
  const [siteChoisi, setSiteChoisi] = useState(prerempli?.site ?? (site === TOUS_LES_SITES ? '' : site));
  const [demiJournee, setDemiJournee] = useState<DemiJournee>(prerempli?.demiJournee ?? 'MATIN');
  const [heureDebut, setHeureDebut] = useState(prerempli?.heureDebut ?? (matin ? '08:00' : '13:00'));
  const [heureFin, setHeureFin] = useState(prerempli?.heureFin ?? (matin ? '12:00' : '17:00'));
  const [lieu, setLieu] = useState('');
  const [formation, setFormation] = useState(prerempli?.formationCode ?? '');
  // Les apprenants d'une session émargent sur la feuille de leur session : la
  // feuille d'une promotion d'apprentissage ne convoque que ses apprentis.
  const [sansSession, setSansSession] = useState(prerempli !== null);
  // Bloc du programme que la séance dispense : il la range dans le taux
  // d'absentéisme par module (TF-461). Il se choisit aussi sur la feuille.
  const [bloc, setBloc] = useState('');
  const blocs = (referentiels.data?.formations.find((entree) => entree.code === formation)?.modules ?? []).filter(
    (module): module is { id: string; libelle: string } => module.id !== undefined,
  );
  const cadre = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (prerempli) cadre.current?.scrollIntoView({ block: 'start' });
    // Le formulaire pré-rempli se montre à son ouverture, une fois.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const complet = libelle.trim().length >= 3 && siteChoisi !== '' && heureFin > heureDebut;

  return (
    <div ref={cadre} className="scroll-mt-24">
      <Carte titre="Nouvelle séance" sansBordure>
        <form
          className="grid grid-cols-1 gap-4 p-4 sm:p-6 md:grid-cols-3"
          onSubmit={(evenement) => {
            evenement.preventDefault();
            if (!complet || enCours) return;
            onCreer({
              libelle: libelle.trim(),
              site: siteChoisi,
              date: jour,
              demiJournee,
              heureDebut,
              heureFin,
              ...(lieu.trim() ? { lieu: lieu.trim() } : {}),
              ...(formation
                ? {
                    formationCode: formation,
                    ...(sansSession ? { sansSession: true } : {}),
                    ...(bloc ? { moduleFormationId: bloc } : {}),
                  }
                : {}),
            });
          }}
        >
          <Champ libelle="Intitulé">
            <input
              value={libelle}
              onChange={(evenement) => setLibelle(evenement.target.value)}
              placeholder="Module 2 · Relation client"
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Site">
            <select
              value={siteChoisi}
              onChange={(evenement) => setSiteChoisi(evenement.target.value)}
              className={CLASSE_CHAMP}
            >
              <option value="">Choisir…</option>
              {(sites?.sites ?? []).map((entree) => (
                <option key={entree.code} value={entree.code}>
                  {entree.nom} ({entree.code})
                </option>
              ))}
            </select>
          </Champ>
          <Champ libelle="Formation">
            <select
              value={formation}
              onChange={(evenement) => {
                setFormation(evenement.target.value);
                setBloc('');
              }}
              className={CLASSE_CHAMP}
            >
              <option value="">Toutes les formations du site</option>
              {(referentiels.data?.formations ?? []).map((entree) => (
                <option key={entree.code} value={entree.code}>
                  {entree.titre} ({entree.code})
                </option>
              ))}
            </select>
          </Champ>
          {blocs.length > 0 && (
            <Champ libelle="Bloc dispensé">
              <select value={bloc} onChange={(evenement) => setBloc(evenement.target.value)} className={CLASSE_CHAMP}>
                <option value="">Hors bloc</option>
                {blocs.map((module) => (
                  <option key={module.id} value={module.id}>
                    {module.libelle}
                  </option>
                ))}
              </select>
            </Champ>
          )}
          <Champ libelle="Demi-journée">
            <select
              value={demiJournee}
              onChange={(evenement) => {
                const valeur = evenement.target.value as DemiJournee;
                setDemiJournee(valeur);
                setHeureDebut(valeur === 'MATIN' ? '08:00' : '13:00');
                setHeureFin(valeur === 'MATIN' ? '12:00' : '17:00');
              }}
              className={CLASSE_CHAMP}
            >
              <option value="MATIN">Matin</option>
              <option value="APRES_MIDI">Après-midi</option>
            </select>
          </Champ>
          <Champ libelle="Début">
            <input
              type="time"
              value={heureDebut}
              onChange={(evenement) => setHeureDebut(evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Fin">
            <input
              type="time"
              value={heureFin}
              onChange={(evenement) => setHeureFin(evenement.target.value)}
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Lieu">
            <input
              value={lieu}
              onChange={(evenement) => setLieu(evenement.target.value)}
              placeholder="Salle A2 · Saint-Benoît"
              className={CLASSE_CHAMP}
            />
          </Champ>
          {/* Une classe à distance a sa salle, qui contrôle la caméra et
              l'émargement : elle ne se crée pas ici. */}
          <p className="min-w-0 self-end rounded-xl bg-gray-50 px-3 py-2.5 text-xs text-codialis-gray md:col-span-2">
            Séance en présentiel. Une classe à distance se programme dans la{' '}
            <Link to="/form-elearning" className="font-semibold text-codialis-sky hover:underline">
              Salle e-learning
            </Link>
            , qui contrôle la caméra et l’émargement.
          </p>

          <div className="space-y-3 md:col-span-3 min-w-0">
            {formation && (
              <label className="flex items-start gap-2 text-sm text-codialis-navy">
                <input
                  type="checkbox"
                  checked={sansSession}
                  onChange={(evenement) => setSansSession(evenement.target.checked)}
                  className="mt-1 rounded accent-codialis-mint"
                />
                <span>
                  Apprentis seulement
                  <span className="block text-[11px] text-codialis-gray">
                    Ceux de la formation sans session : les apprenants d’une session émargent sur sa feuille.
                  </span>
                </span>
              </label>
            )}
            {erreur && <MessageErreur message={erreur} />}
            <BoutonPrincipal type="submit" disabled={!complet || enCours}>
              {enCours ? 'Création…' : 'Créer la séance'}
            </BoutonPrincipal>
            <p className="text-[11px] text-codialis-gray">
              Les apprenants du site qui suivent effectivement le parcours sont convoqués
              automatiquement. La séance porte sur la date affichée en haut de l’écran.
            </p>
          </div>
        </form>
      </Carte>
    </div>
  );
}

function Champ({
  libelle,
  children,
  groupe = false,
}: {
  libelle: string;
  children: React.ReactNode;
  /** Plusieurs commandes (boutons, choix) : un `fieldset`, le libellé en légende. */
  groupe?: boolean;
}): JSX.Element {
  // Le libellé enveloppe son champ : il le nomme sans identifiant à poser
  // (RGAA 11.1, TF-341).
  if (groupe) {
    return (
      <fieldset>
        <legend className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
          {libelle}
        </legend>
        {children}
      </fieldset>
    );
  }
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        {libelle}
      </span>
      {children}
    </label>
  );
}
